import { apolloClient } from '@/graphql/client';
import {
  createFile,
  createSong,
  deleteSong,
  retryFile,
  setSongActiveVersion,
} from '@/graphql/mutations';
import { getSong, getSongs } from '@/graphql/queries';
import { UploadingFile, UploadingState } from '@/types';
import axios from 'axios';
import _ from 'lodash';
import { ActionTree, Dispatch } from 'vuex';

function* batch(records: UploadingFile[], size = 5) {
  while (records.length) {
    yield records.splice(0, size);
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function fakeProcessUploadingFile(
  dispatch: Dispatch,
  uploadFile: UploadingFile,
) {
  setTimeout(() => {
    uploadFile = { ...uploadFile, status: 'FAILED' };
    dispatch('updateUploadingFile', uploadFile);
  }, 3000);
  return Promise.resolve(true);
}

async function processUploadingFile(
  dispatch: Dispatch,
  uploadingFile: UploadingFile,
  state: UploadingState,
) {
  // check if file is still in state: This due to very small edge case
  // where a file as been copied into batch but was removed
  // before processing commenced
  const stateUploadingFile = state.uploadingFiles
    .filter((file: UploadingFile) => {
      return file.id === uploadingFile.id;
    })
    .pop();

  if (!stateUploadingFile) {
    return Promise.reject(false);
  }

  // create abort controller to enable stopping of request
  const abortController = new AbortController();
  const { signal } = abortController;
  uploadingFile = { ...uploadingFile, aborter: abortController };
  dispatch('updateUploadingFile', uploadingFile);

  // create song for the uploading file
  let songId = uploadingFile.songId;
  if (!songId) {
    const createSongResponse = await apolloClient.mutate({
      mutation: createSong,
      variables: {
        title: uploadingFile.file.name,
      },
      context: {
        fetchOptions: {
          signal,
        },
      },
      refetchQueries: [{ query: getSongs }],
    });

    if (!createSongResponse.data.createSong?.id) {
      uploadingFile = { ...uploadingFile, status: 'FAILED', uploading: false };
      dispatch('updateUploadingFile', uploadingFile);
      return;
    }

    songId = createSongResponse.data.createSong.id;

    uploadingFile = {
      ...uploadingFile,
      uploading: true,
      status: 'UPLOADING',
      songId,
    };
    dispatch('updateUploadingFile', uploadingFile);
  }

  // create file and upload url for uploading file
  let fileId = uploadingFile.fileId;
  let uploadUrl;
  if (!fileId) {
    const createFileResponse = await apolloClient.mutate({
      mutation: createFile,
      variables: {
        title: uploadingFile.file.name,
        originalFilename: uploadingFile.file.name,
        songId: songId,
      },
      context: {
        fetchOptions: {
          signal,
        },
      },
      refetchQueries: [{ query: getSong, variables: { id: songId } }],
    });

    if (!createFileResponse.data.createFile?.file?.id) {
      uploadingFile = { ...uploadingFile, status: 'FAILED', uploading: false };
      dispatch('updateUploadingFile', uploadingFile);
      return;
    }

    fileId = createFileResponse.data.createFile.file.id;
    uploadingFile = { ...uploadingFile, fileId };
    dispatch('updateUploadingFile', uploadingFile);

    uploadUrl = createFileResponse.data.createFile.uploadUrl;
  } else {
    const retryFileResponse = await apolloClient.mutate({
      mutation: retryFile,
      variables: {
        id: fileId,
      },
      context: {
        fetchOptions: {
          signal,
        },
      },
    });

    uploadUrl = retryFileResponse.data.retryFile.uploadUrl;
  }

  let fileLoadedPercentage = 0;

  // set uploading file as active version
  await apolloClient.mutate({
    mutation: setSongActiveVersion,
    variables: {
      songId,
      fileId,
    },
    context: {
      fetchOptions: {
        signal,
      },
    },
    refetchQueries: [{ query: getSong, variables: { id: songId } }],
  });

  // commence uploading file to s3 upload url
  return await axios.put(uploadUrl, uploadingFile.file, {
    headers: {
      'Content-Type': '',
    },
    signal,
    onUploadProgress: (progressEvent) => {
      const totalLength = progressEvent.lengthComputable
        ? progressEvent.total
        : progressEvent.target.getResponseHeader('content-length') ||
          progressEvent.target.getResponseHeader(
            'x-decompressed-content-length',
          );

      if (totalLength !== null) {
        fileLoadedPercentage = progressEvent.loaded / totalLength;
        uploadingFile = {
          ...uploadingFile,
          percentage: Math.round(fileLoadedPercentage * 100),
        };
        dispatch('updateUploadingFile', uploadingFile);
        if (fileLoadedPercentage === 1) {
          dispatch('endUploadingFile', uploadingFile);
        }
      }
    },
  });
}

const actions: ActionTree<any, any> = {
  setUploadingFiles({ commit, dispatch, state }, files: UploadingFile[]) {
    const allFiles = _.cloneDeep(files);
    const batchFiles = _.cloneDeep(files);
    commit('setUploadingFiles', allFiles);

    // process files in batch of fives (5)
    setTimeout(() => {
      for (const batchingFiles of batch(batchFiles)) {
        Promise.allSettled(
          batchingFiles.map((batchFile) =>
            processUploadingFile(dispatch, batchFile, state),
          ),
        ).then(() => commit('setLibraryRefresh', true));
      }
    }, 3000);
  },

  updateUploadingFile({ commit }, uploadingFile: UploadingFile) {
    commit('updateUploadingFile', uploadingFile);
  },

  endUploadingFile({ commit }, uploadingFile: UploadingFile) {
    commit('endUploadingFile', uploadingFile);
  },

  async removeUploadingFile({ commit }, uploadingFile: UploadingFile) {
    // abort any request in the upload process
    uploadingFile.aborter?.abort();
    if (uploadingFile.songId) {
      await apolloClient.mutate({
        mutation: deleteSong,
        variables: {
          id: uploadingFile.songId,
        },
        refetchQueries: [{ query: getSongs }],
      });
    }
    commit('removeUploadingFile', uploadingFile);
  },

  async retryUploadingFile({ dispatch, state }, uploadingFile: UploadingFile) {
    const stateUploadingFile = state.uploadingFiles
      .filter((file: UploadingFile) => {
        return file.id === uploadingFile.id;
      })
      .pop();

    if (!stateUploadingFile) {
      return Promise.reject(false);
    }

    return processUploadingFile(dispatch, stateUploadingFile, state);
  },

  async retryFailedUploadingFiles({ dispatch, state }) {
    const failedUploadingFiles = state.uploadingFiles.filter(
      (file: UploadingFile) => {
        return file.status === 'FAILED';
      },
    );

    for (const batchingFiles of batch(failedUploadingFiles)) {
      Promise.allSettled(
        batchingFiles.map((batchFile) =>
          dispatch('retryUploadingFile', batchFile),
        ),
      ).then((results) => results.forEach((result) => console.log(result)));
    }
  },

  async removeFailedUploadingFiles({ dispatch, state }) {
    const failedUploadingFiles = state.uploadingFiles.filter(
      (file: UploadingFile) => {
        return file.status === 'FAILED';
      },
    );

    for (const batchingFiles of batch(failedUploadingFiles)) {
      Promise.allSettled(
        batchingFiles.map((batchFile) =>
          dispatch('removeUploadingFile', batchFile),
        ),
      ).then((results) => results.forEach((result) => console.log(result)));
    }
  },
};

export default actions;
